import { Theme, css } from '@emotion/react';

import { useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import { Radius, Spacing, Typography } from 'chatie-design-system';

import { IEFinderObject } from '@model';

import EFinderImage from './EFinderImage';

const carouselWrapperCSS = css`
  position: relative;

  .carousel .slide {
    background: transparent;
    padding: 0;
  }

  .carousel .slider-wrapper {
    overflow: hidden;
  }

  .carousel .slider {
    align-items: flex-start;
  }
`;

const imageWrapperCSS = css`
  border-radius: ${Radius.medium}px;
  overflow: hidden;
`;

const counterCSS = (theme: Theme) => css`
  position: absolute;
  top: ${Spacing.small}px;
  right: ${Spacing.small}px;
  background-color: rgba(0, 0, 0, 0.5);
  color: ${theme.true.white};
  ${Typography.body12};
  padding: 2px ${Spacing.xsmall}px;
  border-radius: ${Radius.round}px;
  z-index: 2;
  pointer-events: none;
`;

type ImageCarouselProps = {
  images: IEFinderObject<'image'>[];
  onClickImage?: (index: number) => void;
};

const ImageCarousel = ({ images, onClickImage }: ImageCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (images.length === 0) return null;

  if (images.length === 1) {
    const img = images[0];
    return (
      <div
        css={imageWrapperCSS}
        onClick={() => onClickImage?.(0)}
        style={{ cursor: onClickImage ? 'pointer' : undefined }}
      >
        <EFinderImage
          image={img}
          width={img.attachment?.width || 800}
          height={img.attachment?.height || 800}
          layout="responsive"
          objectFit="contain"
        />
      </div>
    );
  }

  return (
    <div
      css={carouselWrapperCSS}
      style={{ cursor: onClickImage ? 'pointer' : undefined }}
    >
      <div css={counterCSS}>
        {currentIndex + 1}/{images.length}
      </div>
      <Carousel
        showThumbs={false}
        showStatus={false}
        showArrows={false}
        showIndicators={false}
        emulateTouch
        preventMovementUntilSwipeScrollTolerance
        swipeScrollTolerance={30}
        infiniteLoop={false}
        selectedItem={currentIndex}
        onChange={setCurrentIndex}
        onClickItem={(index) => onClickImage?.(index)}
      >
        {images.map((image, idx) => (
          <div key={`carousel-img-${image.key || idx}`} css={imageWrapperCSS}>
            <EFinderImage
              image={image}
              width={image.attachment?.width || 800}
              height={image.attachment?.height || 800}
              layout="responsive"
              objectFit="contain"
            />
          </div>
        ))}
      </Carousel>
    </div>
  );
};

export default ImageCarousel;
